import { useCallback, useMemo, useState } from "react";
import type { Fee } from "@dealer-center/ddc-pages-fee-manager-interface";
import type { FeeWithUIState } from "../types";

export const useFeeState = (initialFees: Fee[] = []) => {
  const [serverFees, setServerFees] = useState<Fee[]>(initialFees);
  const [draftFees, setDraftFees] = useState<FeeWithUIState[]>(initialFees);
  const [showRemoved, setShowRemoved] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(draftFees) !== JSON.stringify(serverFees),
    [draftFees, serverFees]
  );

  const activeFees = useMemo(
    () => draftFees.filter(f => !f.removed),
    [draftFees]
  );

  const totalAmountNew = useMemo(
    () =>
      activeFees
        .filter(f => f.type === "NEW" || f.type === "ALL")
        .reduce((sum, f) => sum + (f.amount ?? 0), 0),
    [activeFees]
  );

  const totalAmountUsed = useMemo(
    () =>
      activeFees
        .filter(f => f.type === "USED" || f.type === "ALL")
        .reduce((sum, f) => sum + (f.amount ?? 0), 0),
    [activeFees]
  );

  const setServerAndDraftFees = useCallback((fees: Fee[]) => {
    setServerFees(fees);
    setDraftFees(fees);
  }, []);

  const handleCancel = useCallback(() => {
    setDraftFees(serverFees);
  }, [serverFees]);

  const toggleShowRemoved = useCallback(() => {
    setShowRemoved(prev => !prev);
  }, []);

  const softDelete = useCallback((feeIds: string[]) => {
    const idSet = new Set(feeIds);
    setDraftFees(prevFees =>
      prevFees.map(fee => (idSet.has(fee.id) ? { ...fee, removed: true } : fee))
    );
  }, []);

  const restore = useCallback((feeIds: string[]) => {
    const idSet = new Set(feeIds);
    setDraftFees(prevFees =>
      prevFees.map(fee =>
        idSet.has(fee.id) ? { ...fee, removed: false } : fee
      )
    );
  }, []);

  const updateDraftFee = useCallback(
    (
      feeId: string,
      updates: Partial<Pick<Fee, "name" | "amount" | "type">>
    ) => {
      setDraftFees(prevFees =>
        prevFees.map(fee => (fee.id === feeId ? { ...fee, ...updates } : fee))
      );
    },
    []
  );

  const addDraftFee = useCallback((fee: Fee) => {
    setDraftFees(prevFees => [...prevFees, fee]);
  }, []);

  return {
    serverFees,
    draftFees,
    isDirty,
    showRemoved,
    totalAmountNew,
    totalAmountUsed,
    setServerAndDraftFees,
    handleCancel,
    toggleShowRemoved,
    softDelete,
    restore,
    updateDraftFee,
    addDraftFee
  };
};
